import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Send, Loader2, CheckCircle } from 'lucide-react';
import { useApp } from '../App';
import { createPurchase } from '../lib/api';
import { toast } from 'sonner';
import api from '../lib/api';

const CheckoutModal = ({ open, onClose }) => {
  const { cart, user, fetchCart, fetchRaffles, selectedRaffleId } = useApp();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [telegramLoading, setTelegramLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await createPurchase(selectedRaffleId);
      setSuccess(res.data);
      await fetchCart();
      await fetchRaffles();
      toast.success('¡Compra registrada exitosamente!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al procesar la compra');
    }
    setLoading(false);
  };

  const handleTelegram = async () => {
    setTelegramLoading(true);
    try {
      // Genera enlace de vinculación automático — al abrir Telegram y presionar
      // "Iniciar", la cuenta se conecta y recibe bienvenida en un solo paso
      const res = await api.get('/user/telegram/link');
      if (res.data.ok && res.data.link) {
        window.open(res.data.link, '_blank');
      }
    } catch {
      // Fallback: abrir el bot directamente si falla la generación
      const botUsername = process.env.REACT_APP_TELEGRAM_BOT_USERNAME || 'Cofresmagicosbot';
      window.open(`https://t.me/${botUsername}`, '_blank');
    }
    setTelegramLoading(false);
  };

  const handleClose = () => {
    setSuccess(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#0E1016] border border-white/[0.08] max-w-md mx-auto" data-testid="checkout-modal">
        <DialogHeader>
          <DialogTitle className="text-[#F7F2E7] font-heading text-xl">
            {success ? '¡Compra Registrada!' : 'Confirmar Compra'}
          </DialogTitle>
        </DialogHeader>

        {success ? (
          <div className="space-y-6 py-4">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-green-400" />
              </div>
              <p className="text-[#F7F2E7]/80 text-sm">
                {success.chest_count} cofre{success.chest_count > 1 ? 's' : ''} registrado{success.chest_count > 1 ? 's' : ''}
              </p>
              <p className="text-2xl font-heading font-bold text-[#FFC640] tabular-nums mt-2">
                Total: ${success.total?.toFixed(2)}
              </p>
              {success.user_referral_code && (
                <p className="text-xs text-[#F7F2E7]/50 mt-3">
                  Tu código de referido: <span className="text-[#FFC640] font-semibold">{success.user_referral_code}</span>
                </p>
              )}
            </div>

            <Button
              onClick={handleTelegram}
              disabled={telegramLoading}
              className="w-full h-12 bg-[#229ED9] text-white font-heading font-semibold text-base hover:bg-[#1a8bc2] rounded-xl"
              data-testid="checkout-send-telegram-button"
            >
              {telegramLoading ? (
                <><Loader2 size={20} className="mr-2 animate-spin" /> Conectando...</>
              ) : (
                <><Send size={20} className="mr-2" /> Enviar comprobante por Telegram</>
              )}
            </Button>
            <p className="text-xs text-[#F7F2E7]/40 text-center">
              Se abrirá Telegram. Presiona "Iniciar" y tu cuenta se conectará automáticamente.
            </p>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="bg-[#121521] rounded-xl p-4 border border-white/[0.08] space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-[#F7F2E7]/60">Nombre:</span>
                <span className="text-[#F7F2E7]">{user?.name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#F7F2E7]/60">Teléfono:</span>
                <span className="text-[#F7F2E7]">{user?.phone}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#F7F2E7]/60">Ubicación:</span>
                <span className="text-[#F7F2E7]">{user?.municipality}, {user?.state}</span>
              </div>
            </div>

            <div className="flex items-center justify-between text-sm pt-2">
              <span className="text-[#F7F2E7]/60">{cart.count} cofre{cart.count > 1 ? 's' : ''}</span>
              <span className="font-heading font-bold text-[#FFC640] text-xl tabular-nums">${cart.total?.toFixed(2)}</span>
            </div>

            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full h-12 bg-[#FFC640] text-[#090A0D] font-heading font-semibold text-base hover:bg-[#FFD36B] rounded-xl shadow-[0_10px_28px_rgba(0,0,0,0.45)] disabled:opacity-50"
              data-testid="checkout-submit-button"
            >
              {loading ? (
                <><Loader2 size={18} className="mr-2 animate-spin" /> Procesando...</>
              ) : (
                <><Send size={18} className="mr-2" /> Confirmar y enviar a Telegram</>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CheckoutModal;
